import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Discount as DiscountIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useTranslate } from "@refinedev/core";
import { StripeSubscription, SubscriptionStatus, SubscriptionDiscount } from "../../../interfaces/property";
import { apiJson, apiVersion } from "../../../utils/api";
import { trackBillingAction } from "../../../utils/sentry-breadcrumbs";
import { trackPropertySubscriptionInteraction } from "../../../utils/analytics-events";

interface SubscriptionCardProps {
  subscription: StripeSubscription;
  propertyUid: string;
  canManageSubscription: boolean;
  onActionComplete?: () => void;
}

const getStatusColor = (status: SubscriptionStatus): "success" | "error" | "warning" | "info" | "default" => {
  switch (status) {
    case "active":
      return "success";
    case "canceled":
    case "unpaid":
    case "incomplete_expired":
      return "error";
    case "trialing":
    case "past_due":
    case "paused":
      return "warning";
    case "incomplete":
      return "info";
    default:
      return "default";
  }
};

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100); // Stripe amounts are in cents
};

const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getBillingCycle = (interval: string, intervalCount: number): string => {
  if (intervalCount === 1) {
    return interval;
  }
  return `${intervalCount} ${interval}s`;
};

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  subscription, 
  propertyUid, 
  canManageSubscription,
  onActionComplete 
}) => {
  const t = useTranslate();
  
  // Track subscription card view on mount
  React.useEffect(() => {
    trackPropertySubscriptionInteraction(propertyUid, undefined, 'view');
  }, [propertyUid]);
  
  // Check if subscription has any active discounts
  const discounts = (subscription.discounts || []).concat(subscription.discount ? [subscription.discount] : []);
  const resolvedDiscounts = discounts
    .map((d) => {
      if (!d) return null;
      const coupon = (d as any).coupon ?? (d as any).source?.coupon ?? null;
      return coupon ? { ...d, coupon } as SubscriptionDiscount : null;
    })
    .filter(Boolean) as SubscriptionDiscount[];
  const hasDiscount = resolvedDiscounts.length > 0;
  const [expanded, setExpanded] = useState(true);
  
  // Dialog states
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [cancelDiscountDialogOpen, setCancelDiscountDialogOpen] = useState(false);
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);
  
  // Action states
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);
  const [immediately, setImmediately] = useState(false);
  const [draft, setDraft] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = () => setExpanded((prev) => !prev);

  const formatDiscountValue = (coupon: SubscriptionDiscount["coupon"] | undefined | null, currency: string): string => {
    if (!coupon) return t("properties.subscription.discounts.unknown");
    if (typeof coupon.percent_off === "number") {
      return `${coupon.percent_off}% ${t("properties.subscription.discounts.off")}`;
    }
    if (typeof coupon.amount_off === "number") {
      return formatCurrency(coupon.amount_off, coupon.currency || currency);
    }
    return coupon.name || coupon.id || t("properties.subscription.discounts.unknown");
  };

  const handleGiveDiscount = async () => {
    setIsLoading(true);
    setError(null);
    trackBillingAction("give_discount", { propertyUid, months: selectedMonths });
    trackPropertySubscriptionInteraction(propertyUid, undefined, 'give_discount');
    try {
      await apiJson(
        `admin/${apiVersion}/properties/${propertyUid}/discount`,
        {
          method: "POST",
          body: JSON.stringify({ months: selectedMonths }),
        }
      );
      setDiscountDialogOpen(false);
      onActionComplete?.();
    } catch (err) {
      setError(t("properties.subscription.errors.giveDiscountFailed"));
      console.error("Failed to give discount:", err);
      // Capture billing error with critical priority
      Sentry.captureException(err, {
        tags: {
          priority: "critical",
          context: "subscription",
          action: "give_discount",
        },
        extra: {
          propertyUid,
          months: selectedMonths,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDiscount = async () => {
    setIsLoading(true);
    setError(null);
    trackBillingAction("cancel_discount", { propertyUid });
    trackPropertySubscriptionInteraction(propertyUid, undefined, 'cancel_discount');
    try {
      await apiJson(
        `admin/${apiVersion}/properties/${propertyUid}/discount`,
        {
          method: "DELETE",
        }
      );
      setCancelDiscountDialogOpen(false);
      onActionComplete?.();
    } catch (err) {
      setError(t("properties.subscription.errors.cancelDiscountFailed"));
      console.error("Failed to cancel discount:", err);
      // Capture billing error with critical priority
      Sentry.captureException(err, {
        tags: {
          priority: "critical",
          context: "subscription",
          action: "cancel_discount",
        },
        extra: {
          propertyUid,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);
    setError(null);
    trackBillingAction("cancel_subscription", { propertyUid, immediately, draft });
    trackPropertySubscriptionInteraction(propertyUid, undefined, 'cancel_subscription');
    try {
      const queryParams = new URLSearchParams();
      if (immediately) queryParams.append("immediately", "true");
      if (draft) queryParams.append("draft", "true");
      
      const url = `admin/${apiVersion}/properties/${propertyUid}/cancel${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      await apiJson(url, {
        method: "POST",
      });
      setCancelSubscriptionDialogOpen(false);
      onActionComplete?.();
    } catch (err) {
      setError(t("properties.subscription.errors.cancelSubscriptionFailed"));
      console.error("Failed to cancel subscription:", err);
      // Capture subscription cancellation error with critical priority
      Sentry.captureException(err, {
        tags: {
          priority: "critical",
          context: "subscription",
          action: "cancel_subscription",
        },
        extra: {
          propertyUid,
          immediately,
          draft,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">
              {t("properties.subscription.title")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={t(`properties.subscription.status.${subscription.status}`)}
              color={getStatusColor(subscription.status)}
              size="medium"
            />
            <IconButton onClick={handleToggle} size="small" aria-label="toggle subscription details" sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid container spacing={2}>
          {/* Plan Name */}
          {subscription.plan.nickname && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t("properties.subscription.fields.plan")}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {subscription.plan.nickname}
              </Typography>
            </Grid>
          )}

          {/* Amount */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              {t("properties.subscription.fields.amount")}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatCurrency(subscription.plan.amount, subscription.plan.currency)}
            </Typography>
          </Grid>

          {/* Billing Cycle */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              {t("properties.subscription.fields.billingCycle")}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {t(`properties.subscription.interval.${getBillingCycle(subscription.plan.interval, subscription.plan.interval_count)}`)}
            </Typography>
          </Grid>

          {/* Current Period */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              {t("properties.subscription.fields.currentPeriod")}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(subscription.items.data[0].current_period_start)} - {formatDate(subscription.items.data[0].current_period_end)}
            </Typography>
          </Grid>

          {/* Trial Period */}
          {subscription.trial_end && subscription.status === "trialing" && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t("properties.subscription.fields.trialEnds")}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(subscription.trial_end)}
              </Typography>
            </Grid>
          )}

          {/* Renewal or Cancellation */}
          {subscription.cancel_at_period_end ? (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ backgroundColor: "error.lighter", p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="error.main" fontWeight={500}>
                  {t("properties.subscription.fields.cancelAt")} {formatDate(subscription.items.data[0].current_period_end)}
                </Typography>
              </Box>
            </Grid>
          ) : subscription.status === "active" && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t("properties.subscription.fields.renewsOn")}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(subscription.items.data[0].current_period_end)}
              </Typography>
            </Grid>
          )}

          {/* Canceled At */}
          {subscription.canceled_at && subscription.status === "canceled" && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t("properties.subscription.fields.canceledAt")}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(subscription.canceled_at)}
              </Typography>
            </Grid>
          )}

          {/* Discounts */}
          {hasDiscount && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("properties.subscription.discounts.title")}
              </Typography>
              <Grid container spacing={1}>
                {resolvedDiscounts.map((discount) => {
                  const coupon = discount.coupon;
                  const durationLabel = coupon.duration === "repeating" && coupon.duration_in_months
                    ? `${coupon.duration_in_months} ${t("properties.subscription.discounts.months")}`
                    : coupon.duration === "once"
                      ? t("properties.subscription.discounts.once")
                      : t("properties.subscription.discounts.forever");
                  return (
                    <Grid item xs={12} sm={6} md={4} key={discount.id}>
                      <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {coupon.name || coupon.id || t("properties.subscription.discounts.unknown")}
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {formatDiscountValue(coupon, subscription.plan.currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {durationLabel}
                        </Typography>
                        {discount.end && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t("properties.subscription.discounts.ends")} {formatDate(discount.end)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}

          {/* Subscription ID */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {t("properties.subscription.fields.subscriptionId")}: {subscription.id}
            </Typography>
          </Grid>

          {/* Action Buttons - Only for admin/founder */}
          {canManageSubscription && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DiscountIcon />}
                  onClick={() => { setSelectedMonths(null); setDiscountDialogOpen(true); }}
                  disabled={hasDiscount}
                >
                  {t("properties.subscription.actions.giveDiscount")}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CloseIcon />}
                  onClick={() => setCancelDiscountDialogOpen(true)}
                  disabled={!hasDiscount}
                >
                  {t("properties.subscription.actions.cancelDiscount")}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelSubscriptionDialogOpen(true)}
                  disabled={subscription.status !== "active" && subscription.status !== "trialing"}
                >
                  {t("properties.subscription.actions.cancelSubscription")}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
        </Collapse>
      </CardContent>
    </Card>

      {/* Give Discount Dialog */}
      <Dialog open={discountDialogOpen} onClose={() => !isLoading && setDiscountDialogOpen(false)}>
        <DialogTitle>{t("properties.subscription.dialogs.giveDiscount.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("properties.subscription.dialogs.giveDiscount.description")}
          </DialogContentText>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("properties.subscription.dialogs.giveDiscount.selectMonths")}
          </Typography>
          
          <ToggleButtonGroup
            value={selectedMonths}
            exclusive
            onChange={(e, value) => value !== null && setSelectedMonths(value)}
            fullWidth
          >
            <ToggleButton value={3}>3 {t("properties.subscription.dialogs.giveDiscount.months")}</ToggleButton>
            <ToggleButton value={6}>6 {t("properties.subscription.dialogs.giveDiscount.months")}</ToggleButton>
            <ToggleButton value={12}>12 {t("properties.subscription.dialogs.giveDiscount.months")}</ToggleButton>
          </ToggleButtonGroup>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountDialogOpen(false)} disabled={isLoading}>
            {t("buttons.cancel")}
          </Button>
          <Button 
            onClick={handleGiveDiscount} 
            variant="contained" 
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {t("buttons.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Discount Dialog */}
      <Dialog open={cancelDiscountDialogOpen} onClose={() => !isLoading && setCancelDiscountDialogOpen(false)}>
        <DialogTitle>{t("properties.subscription.dialogs.cancelDiscount.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("properties.subscription.dialogs.cancelDiscount.description")}
          </DialogContentText>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDiscountDialogOpen(false)} disabled={isLoading}>
            {t("buttons.cancel")}
          </Button>
          <Button 
            onClick={handleCancelDiscount} 
            variant="contained" 
            color="warning"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {t("buttons.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelSubscriptionDialogOpen} onClose={() => !isLoading && setCancelSubscriptionDialogOpen(false)}>
        <DialogTitle>{t("properties.subscription.dialogs.cancelSubscription.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("properties.subscription.dialogs.cancelSubscription.description")}
          </DialogContentText>

          <FormControlLabel
            control={
              <Checkbox
                checked={immediately}
                onChange={(e) => setImmediately(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={t("properties.subscription.dialogs.cancelSubscription.immediately")}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={draft}
                onChange={(e) => setDraft(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={t("properties.subscription.dialogs.cancelSubscription.draft")}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelSubscriptionDialogOpen(false)} disabled={isLoading}>
            {t("buttons.cancel")}
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            variant="contained" 
            color="error"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {t("buttons.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
